<template>
    <nav id="nav" class="navbar navbar-expand-lg navbar-dark bg-dark">
        <div ref="logo" class="ms-2 logoarea me-5">
            <img src="/img/AuggieIcon50.png" alt="Auggie">
        </div>
        <help horizontal="inline" />
        <div v-if="isDebug()" class="debugarea">
            SignalR <input type="checkbox" v-model="signalrEnabled" class="me-1">
            API <input type="checkbox" v-model="apisEnabled" class="me-1">
            <button-button type="button" class="btn btn-secondary nobounce ms-1" @click.prevent="debugActionClicked">Test</button-button>
        </div>
        <div v-if="cellularActive()" :class="getCellularCss() + ' ms-auto me-5 cellular ' + cellularLevelCss()" @click.prevent="cellularClicked">
            <svg class="cellular-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="0"  y="18" width="3" height="6"  :opacity="barFilled(1) ? 1 : 0.25" />
                <rect x="5"  y="14" width="3" height="10" :opacity="barFilled(2) ? 1 : 0.25" />
                <rect x="10" y="10" width="3" height="14" :opacity="barFilled(3) ? 1 : 0.25" />
                <rect x="15" y="6"  width="3" height="18" :opacity="barFilled(4) ? 1 : 0.25" />
                <rect x="20" y="2"  width="3" height="22" :opacity="barFilled(5) ? 1 : 0.25" />
            </svg>
            <span v-if="isRoaming()" class="roaming-badge ms-1">R</span>
        </div>

        <div :class="getNetworkCss() + ' ' + (cellularActive() ? '' : 'ms-auto ') + 'me-5 wifi'" @click.prevent="networkClicked">
            <span v-if="wifiActive()">
                <fa-icon icon="wifi" :class="networkStatusCss" />
                WiFi
            </span>
            <span v-if="networkActive()">
                <fa-icon icon="network-wired" :class="networkStatusCss" />
                Network
            </span>
        </div>

        <div class="me-5 battery" :class="batteryLevelCss()" v-if="hasBatteryInfo()">
            <fa-icon :icon="batteryLevelIcon()" />
            <div class="percentage ms-1">
                {{ batteryPercentage() }}%
            </div>
        </div>
    </nav>
</template>

<style scoped lang="scss">
    .logoarea {
        cursor: default;
        line-height:0;
    }
    .wifi {
        font-size: 1.5rem;
    }
    .cellular {
        display: flex;
        align-items: center;
        font-size: 1.5rem;
        .cellular-icon {
            height: 1.5rem;
        }
        .roaming-badge {
            font-size: 0.9rem;
            font-weight: bold;
        }
    }
    .battery {
        display:flex;
        align-items: center;
        font-size: 2rem;
        .percentage {
            font-size: 1.0rem;
        }
    }
    .pointerCursor {
        cursor: pointer;
    }
    .defaultCursor {
        cursor: default;
    }
    .debugarea,
    .debugarea button {
        font-size:16px;
    }
</style>

<script lang="ts">
import { defineComponent } from "vue";
import "@/services/Auggie";
import { AUGGIEAVAILABLE } from "@/main";
import { Global } from "@/support/GlobalData";
import { SignalR, SignalRSupport } from "@/support/SignalR";
import { DataRequest } from "@/shared/support/Data";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";

export default defineComponent({
    name: "Header",
    data() {
        return {
            pingOkBelow: Number(import.meta.env.VITE_PINGOKBELOW!),
            pingBadAbove: Number(import.meta.env.VITE_PINGBADABOVE!),
            networkStatusCss: "text-success me-1",
            debugCounting: false,
            debugClicks: 0,
            debugTimeout: 0
        };
    },
    computed: {
        signalrEnabled: {
            get(): boolean {
                return SignalRSupport.Enabled;
            },
            set(value: boolean) {
                SignalRSupport.Enabled = value;
            }
        },
        apisEnabled: {
            get(): boolean {
                return DataRequest.Enabled;
            },
            set(value: boolean) {
                DataRequest.Enabled = value;
            }
        }
    },
    methods: {
        isDebug(): boolean {
            return import.meta.env.VITE_DEBUG === "1";
        },
        debugActionClicked(): void {
            CalibrationEventSupport.sendEventDeviceFailure("Something failed.");
            CalibrationEventSupport.sendEvent(CalibrationEvent.AcknowledgeByDevice);
        },
        wifiActive(): boolean {
            if (Global.IsDemo()) return true;
            if (AUGGIEAVAILABLE && Global.IsVersionAtLeast("2023.133.0"))
                return Auggie.Network.CurrentNetworkType() === "wifi" || Auggie.Network.CurrentNetworkType() !== "ethernet";
            return true;
        },
        networkActive(): boolean {
            if (Global.IsDemo()) return false;
            if (AUGGIEAVAILABLE && Global.IsVersionAtLeast("2023.133.0"))
                return Auggie.Network.CurrentNetworkType() === "ethernet";
            return false;
        },
        networkClicked(): void {
            if (Global.MainApp.haveSession()) return;
            if (Global.IsDemo()) return;

            if (AUGGIEAVAILABLE)
                Auggie.System.WifiPage();
            else
                globalThis.location.assign(import.meta.env.VITE_STATICWIFI!);
        },
        debugClicked(): void {
            if (Global.MainApp.haveSession()) return;

            if (AUGGIEAVAILABLE)
                Auggie.System.DebugPage();
            else
                globalThis.location.assign(import.meta.env.VITE_STATICDEBUG!);
        },
        cellularActive(): boolean {
            if (Global.IsDemo()) return false;
            if (!AUGGIEAVAILABLE) return false;
            if (!Global.IsVersionAtLeast(Global.CellularPackageVersion)) return false;
            // cellInfo.ps1 returns empty on non-AIR, so a populated CellularInfo is
            // the reliable "AIR device with cellular hardware" signal.
            if (Global.CellularInfo !== null) return true;
            // cellInfo.ps1 may briefly return empty during modem init (up to 2 min
            // cadence). Any non-zero cellSignal value proves AIR hardware because
            // non-AIR always emits "0,0", so we can render the icon while waiting
            // for the first cellInfo payload.
            const state = Global.CellularSignal?.State ?? 0;
            const signal = Global.CellularSignal?.Signal ?? 0;
            return state === 1 || signal > 0;
        },
        cellularLevelCss(): string {
            const state = Global.CellularSignal?.State ?? 0;
            const signal = Global.CellularSignal?.Signal ?? 0;
            if (state !== 1) return "text-secondary";
            if (signal >= 61) return "text-success";
            if (signal >= 21) return "text-warning";
            return "text-danger";
        },
        barsFilled(): number {
            // When not connected, render all bars empty regardless of reported signal.
            if (Global.CellularSignal?.State !== 1) return 0;
            const signal = Global.CellularSignal?.Signal ?? 0;
            if (signal >= 81) return 5;
            if (signal >= 61) return 4;
            if (signal >= 41) return 3;
            if (signal >= 21) return 2;
            if (signal >= 1)  return 1;
            return 0;
        },
        barFilled(n: number): boolean {
            return n <= this.barsFilled();
        },
        isRoaming(): boolean {
            return Global.CellularInfo?.Roaming === "Yes";
        },
        cellularClicked(): void {
            if (Global.MainApp.haveSession()) return;
            if (Global.IsDemo()) return;

            if (AUGGIEAVAILABLE)
                Auggie.System.CellularPage();
            else
                globalThis.location.assign(import.meta.env.VITE_STATICCELLULAR!);
        },
        getCellularCss(): string {
            if (Global.MainApp.haveSession()) return "defaultCursor";
            return "pointerCursor";
        },
        getNetworkCss(): string {
            if (Global.MainApp.haveSession()) return "defaultCursor";
            return "pointerCursor";
        },
        updateNetworkStatus(): void {
            if (Global.IsDemo()) return;
            if (!SignalR.isConnected || SignalR.isReconnecting) {
                this.setNetworkStatus("text-danger");
            } else {
                this.pingTest();
            }
        },
        setNetworkStatus(status: string): void {
            if (status === "")
                status = "text-success";
            this.networkStatusCss = `${status} me-1`;
        },
        pingTest(): number {
            const dr = new DataRequest();
            dr.autoToastOnFailure = false;

            const start = performance.now();

            dr.$getPure("/Ping")
                .then((data: string): void => {
                    const end = performance.now();
                    const ms = end - start;
                    if (ms < this.pingOkBelow) {
                        this.setNetworkStatus("text-success");
                    } else if (ms >= this.pingOkBelow && ms <= this.pingBadAbove) {
                        this.setNetworkStatus("text-warning");
                    } else {
                        this.setNetworkStatus("text-danger");
                    }
                    if (Global.DetailLog) console.log(`Ping test responded within ${ms.toFixed(2)} ms`);
                })
                .catch((e: any): void => {
                    this.setNetworkStatus("text-danger");
                });
            return 200;
        },
        hasBatteryInfo(): boolean {
            return !!Global.BatteryInfo;
        },
        batteryPercentage(): string {
            if (!Global.BatteryInfo) return "";
            return Global.BatteryInfo.PercentageFull.toFixed(0);
        },
        batteryLevelCss(): string {
            if (!Global.BatteryInfo) return "";
            if (Global.BatteryInfo.PercentageFull >= 51)
                return "text-success";
            if (Global.BatteryInfo.PercentageFull >= 21)
                return "text-warning";
            return "text-danger";
        },
        batteryLevelIcon(): string {
            if (!Global.BatteryInfo) return "";
            if (Global.BatteryInfo.ExternalPowerInd) {
                return "bolt";
            } else {
                if (Global.BatteryInfo.PercentageFull >= 90)
                    return "battery-full";
                if (Global.BatteryInfo.PercentageFull >= 70)
                    return "battery-three-quarters";
                if (Global.BatteryInfo.PercentageFull >= 50)
                    return "battery-half";
                if (Global.BatteryInfo.PercentageFull >= 20)
                    return "battery-quarter";
                return "battery-empty";
            }
        },
        handleDebugClicks(): void {
            (this.$refs.logo as HTMLDivElement).addEventListener("click", this.debugClickListener, false);
        },
        debugClickListener(e: Event): void {
            if (Global.MainApp.haveSession()) return;
            this.debugClicks++;
            if (!this.debugCounting) {
                this.debugCounting = true;
                this.debugTimeout = setTimeout((): void => {
                    this.debugCounting = false;
                    this.debugClicks = 0;
                    this.debugTimeout = 0;
                }, 5000) as unknown as number;
            } else if (this.debugClicks >= 5) {
                this.debugClicked();
                if (this.debugTimeout)
                    clearTimeout(this.debugTimeout);
                this.debugTimeout = 0;
                this.debugCounting = false;
                this.debugClicks = 0;
                this.debugTimeout = 0;
            }
        },
        update(): void {
            this.$forceUpdate();
        }
    },
    created(): void {
        Global.MainHeader = this;
    },
    mounted(): void {
        this.handleDebugClicks();
        SignalR.onConnectionStartedHeader = (): void => { this.updateNetworkStatus(); };
        SignalR.onConnectionEndedHeader = (): void => { this.updateNetworkStatus(); };
        const pingInterval = Number(import.meta.env.VITE_PINGINTERVAL!);
        if (!Global.IsDemo() && pingInterval && pingInterval > 0) {
            setInterval((): void => {
                this.updateNetworkStatus();
            }, pingInterval * 1000);
        }
    },
    unmounted(): void {
        SignalR.onConnectionStartedHeader = null;
        SignalR.onConnectionEndedHeader = null;
    }
});

</script>
